import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ShippingPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <section className="pt-20 md:pt-24 pb-8 md:pb-12 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <a href="/" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1">
                <i className="ri-home-line w-3 h-3 flex items-center justify-center" /> Home
              </a>
              <span className="text-foreground-300">/</span>
              <span className="text-xs text-primary-600 font-medium">Shipping</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              CSSBuy <span className="text-primary-500">Shipping Cost</span> Guide
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
              Understand the factors that influence CSSBuy shipping costs, including weight, volume, destination, and shipping methods.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full py-12 md:py-16">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8 lg:p-10">
              <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                <p className="mb-4">
                  Understanding <strong>CSSBuy shipping costs</strong> is essential for planning your purchases. The final shipping price depends on multiple factors that every buyer should know before placing an order.
                </p>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Factors That Affect Shipping Cost</h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-xs md:text-sm border border-background-200 rounded-lg overflow-hidden">
                    <thead className="bg-background-100">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">Factor</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">Description</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">Impact</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-background-200">
                        <td className="px-4 py-3 text-foreground-700 font-medium">Weight</td>
                        <td className="px-4 py-3 text-foreground-600">Actual weight of the parcel</td>
                        <td className="px-4 py-3 text-foreground-600">Direct cost multiplier</td>
                      </tr>
                      <tr className="border-b border-background-200 bg-background-50/50">
                        <td className="px-4 py-3 text-foreground-700 font-medium">Volumetric Weight</td>
                        <td className="px-4 py-3 text-foreground-600">Calculated from dimensions</td>
                        <td className="px-4 py-3 text-foreground-600">Often higher than actual weight</td>
                      </tr>
                      <tr className="border-b border-background-200">
                        <td className="px-4 py-3 text-foreground-700 font-medium">Destination</td>
                        <td className="px-4 py-3 text-foreground-600">Country and region</td>
                        <td className="px-4 py-3 text-foreground-600">Varies by route</td>
                      </tr>
                      <tr className="border-b border-background-200 bg-background-50/50">
                        <td className="px-4 py-3 text-foreground-700 font-medium">Shipping Method</td>
                        <td className="px-4 py-3 text-foreground-600">Express, standard, economy</td>
                        <td className="px-4 py-3 text-foreground-600">Speed vs. cost trade-off</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-foreground-700 font-medium">Insurance</td>
                        <td className="px-4 py-3 text-foreground-600">Optional parcel protection</td>
                        <td className="px-4 py-3 text-foreground-600">Additional fee</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Shipping Methods Overview</h2>
                <p className="mb-4">
                  CSSBuy typically offers multiple shipping options ranging from economy sea mail to express air freight. Each method has different delivery times, tracking capabilities, and cost structures. The best choice depends on your budget, timeline, and the value of your items.
                </p>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                  <h4 className="font-semibold text-sm text-amber-800 mb-2 flex items-center gap-2">
                    <i className="ri-information-line w-4 h-4 flex items-center justify-center" />
                    Important Note
                  </h4>
                  <p className="text-xs text-amber-700">
                    Final shipping costs can vary by weight, dimensions, destination, route, and current pricing. Always verify the latest shipping rates directly on the CSSBuy platform before confirming your order.
                  </p>
                </div>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Tips to Reduce Shipping Costs</h2>
                <ul className="space-y-2 mb-6 list-disc list-inside">
                  <li><strong>Consolidate parcels</strong> — Combine multiple items into one shipment to reduce per-item costs.</li>
                  <li><strong>Remove packaging</strong> — Request removal of unnecessary boxes and tags to reduce weight.</li>
                  <li><strong>Choose slower methods</strong> — Economy shipping is significantly cheaper for non-urgent orders.</li>
                  <li><strong>Check volumetric rules</strong> — Some items are light but bulky; understand how volumetric weight is calculated.</li>
                </ul>
                <div className="p-5 bg-primary-50 border border-primary-200 rounded-xl mt-8">
                  <p className="text-sm text-primary-800 font-medium mb-3">Ready to calculate shipping?</p>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors">
                    Visit CSSBuy Spreadsheet
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}